import { useState } from 'react';
import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { RoleMapMenuOption } from './RoleMapFilterButton';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1837: the selection toolbar of `role-map-table.html` — "Đã chọn N dòng",
 * "Áp dụng cho cả N:", the three action chips (Đổi phạm vi / Đồng bộ lại / Xoá)
 * and "Bỏ chọn". Copy verbatim.
 *
 * Two counts are deliberately shown, not one. N is the number of VISUAL rows
 * ticked; a row can stand for two or more `RoleMap` ids (a multi-role mapping),
 * and every action here sends all of them — so when the two numbers differ the
 * bar spells out how many links are really about to change. Hiding that would
 * let "Xoá 2 dòng" quietly deactivate four links.
 */
export function RoleMapBulkBar({
    vm,
    onRequestDelete,
}: {
    vm: RoleMapTableVM;
    onRequestDelete: () => void;
}) {
    const { t } = useTranslation();
    const [scopeOpen, setScopeOpen] = useState(false);

    if (vm.selectedCount === 0) return null;

    const expands = vm.selectedIdCount !== vm.selectedCount;

    return (
        <div className="rm-bulk-bar">
            <span className="rm-bulk-bar__count">
                {t('console.roleMap.bulkBar.selectedCount', { count: vm.selectedCount })}
            </span>
            <span className="rm-bulk-bar__divider" />
            <span className="rm-bulk-bar__apply">
                {t('console.roleMap.bulkBar.applyToAll', { count: vm.selectedCount })}
            </span>

            <Popover
                trigger="click"
                open={scopeOpen}
                onOpenChange={setScopeOpen}
                placement="bottomLeft"
                overlayInnerStyle={{ padding: 0 }}
                content={
                    <div className="rm-menu">
                        {/* EAA-1837: `RoleMapBulkScopeSerializer` accepts ONLY null
                            (ecosystem default) or the org id already in the URL — a
                            different org's id is refused with VALIDATION_ERROR. So
                            this is a two-option choice by contract, never an org
                            search.

                            The org option is rendered CHECKED AND INERT rather than
                            actionable: the bulk bar only ever appears while the org
                            slice is shown, so every selected row already carries
                            this org's id and "apply" would be a request that
                            changes nothing yet reports success. Showing it keeps
                            the export's two-option shape while saying which one is
                            already true. */}
                        <span className="rm-menu__label">
                            {t('console.roleMap.bulkBar.scopeMenuLabel')}
                        </span>
                        <RoleMapMenuOption
                            checked={false}
                            label={t('console.roleMap.bulkBar.scopeDefaultLabel')}
                            hint={t('console.roleMap.bulkBar.scopeDefaultHint')}
                            onToggle={() => {
                                setScopeOpen(false);
                                vm.applyBulkScope('default');
                            }}
                        />
                        <RoleMapMenuOption
                            checked
                            pinned
                            label={vm.actingOrgName ?? t('console.roleMap.bulkBar.scopeOrgFallback')}
                            hint={t('console.roleMap.bulkBar.scopeOrgHint')}
                            title={t('console.roleMap.bulkBar.scopeOrgTitle')}
                            onToggle={() => undefined}
                        />
                    </div>
                }
            >
                <button type="button" className="rm-bulk-bar__action" disabled={vm.bulkBusy}>
                    {t('console.roleMap.bulkBar.changeScope')}
                </button>
            </Popover>

            <button
                type="button"
                className="rm-bulk-bar__action"
                onClick={vm.applyBulkResync}
                disabled={vm.bulkBusy}
                title={t('console.roleMap.bulkBar.resyncTitle')}
            >
                {t('console.roleMap.bulkBar.resync')}
            </button>
            <button
                type="button"
                className="rm-bulk-bar__action is-danger"
                onClick={onRequestDelete}
                disabled={vm.bulkBusy}
            >
                {t('common.delete')}
            </button>

            {expands && (
                <span className="rm-bulk-bar__expands">
                    {t('console.roleMap.bulkBar.expandsText', { count: vm.selectedIdCount })}
                </span>
            )}

            <span className="rm-bulk-bar__spacer" />
            <button
                type="button"
                className="rm-bulk-bar__clear"
                onClick={vm.clearSelection}
                disabled={vm.bulkBusy}
            >
                {t('console.roleMap.bulkBar.clearSelection')}
            </button>
        </div>
    );
}
